import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MdSearch, MdRssFeed, MdLocationOn, MdInventory, MdVolumeUp, MdVolumeOff, MdStop } from 'react-icons/md';
import api from '../api/axios';
import { useScanStream } from '../lib/useScanStream';

// ── RSSI → distance category. Thresholds come from the backend (per-reader
// or global default). Falls back to typical UHF values if no bands provided.
const DEFAULT_BANDS = { very_close: -45, close: -55, near: -65, medium: -75, far: -85 };
const rssiBand = (rssi, bands = DEFAULT_BANDS) => {
  if (rssi == null) return null;
  if (rssi >= bands.very_close) return { label: 'VERY CLOSE',  pct: 95, color: '#2e7d32' };
  if (rssi >= bands.close)      return { label: 'CLOSE',       pct: 80, color: '#43a047' };
  if (rssi >= bands.near)       return { label: 'NEAR',        pct: 60, color: '#fbc02d' };
  if (rssi >= bands.medium)     return { label: 'MEDIUM',      pct: 40, color: '#fb8c00' };
  if (rssi >= bands.far)        return { label: 'FAR',         pct: 20, color: '#e53935' };
  return                                { label: 'VERY FAR',   pct: 8,  color: '#c62828' };
};

// ── Audio: a "geiger counter" tick that gets faster as RSSI improves ────────
class GeigerEngine {
  constructor() {
    this.ctx = null;
    this.timer = null;
    this.enabled = false;
  }
  enable() {
    if (this.ctx) return;
    try {
      this.ctx = new (window.AudioContext || window.webkitAudioContext)();
      this.enabled = true;
    } catch { this.enabled = false; }
  }
  disable() {
    this.enabled = false;
    this.stop();
    if (this.ctx) { try { this.ctx.close(); } catch {} this.ctx = null; }
  }
  stop() { if (this.timer) { clearTimeout(this.timer); this.timer = null; } }

  // Schedules clicks based on RSSI. Faster + higher pitched = closer.
  setRssi(rssi) {
    if (!this.enabled || !this.ctx) return;
    this.stop();
    if (rssi == null) return;

    // Map -90..-30 dBm → interval 800..40 ms, frequency 280..1400 Hz
    const clamped = Math.min(-30, Math.max(-90, rssi));
    const t = (clamped + 90) / 60;                    // 0..1
    const interval = Math.round(800 - 760 * t);
    const freq = Math.round(280 + 1120 * t);
    this._click(freq);
    this.timer = setTimeout(() => this.setRssi(rssi), interval);
  }

  _click(freq) {
    if (!this.enabled || !this.ctx) return;
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    osc.connect(gain); gain.connect(this.ctx.destination);
    osc.type = 'square';
    osc.frequency.setValueAtTime(freq, this.ctx.currentTime);
    gain.gain.setValueAtTime(0.0001, this.ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.20, this.ctx.currentTime + 0.005);
    gain.gain.exponentialRampToValueAtTime(0.0001, this.ctx.currentTime + 0.05);
    osc.start();
    osc.stop(this.ctx.currentTime + 0.06);
  }
}

const POLL_MS = 350;

const AssetSearch = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [target, setTarget] = useState(null);          // asset we're hunting for
  const [tracking, setTracking] = useState(false);
  const [proximity, setProximity] = useState(null);    // { rssi, age_seconds, stale, ... }
  const [audioOn, setAudioOn] = useState(false);
  const [loading, setLoading] = useState(false);
  const [radarAngle, setRadarAngle] = useState(0);

  const pollRef = useRef(null);
  const geigerRef = useRef(null);

  // Lazy-init the geiger engine
  if (!geigerRef.current) geigerRef.current = new GeigerEngine();

  // ── Real-time WS subscription for the active target ─────────────────────
  const wsTopic = target?.rfid_tag ? `asset:${target.rfid_tag}` : null;
  const { lastEvent: wsEvent, connected: wsConnected } = useScanStream(wsTopic);

  // Whenever a WS event arrives, refresh proximity from it directly.
  useEffect(() => {
    if (!wsEvent || !target?.rfid_tag) return;
    if (wsEvent.tag_number !== target.rfid_tag) return;
    setProximity({
      found: true, stale: false, age_seconds: 0,
      rssi: wsEvent.rssi, timestamp: wsEvent.timestamp,
      session_id: wsEvent.session_id,
      scanned_by_name: wsEvent.scanned_by_name,
    });
    if (audioOn && geigerRef.current) geigerRef.current.setRssi(wsEvent.rssi);
  }, [wsEvent, target, audioOn]);

  // ── Search (partial: name, tag, RFID, serial, barcode) ────────────────────
  const debounceRef = useRef(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query.trim()) { setResults([]); return; }
    debounceRef.current = setTimeout(async () => {
      try {
        // Ask for what we display. This used to request every match of a
        // one-letter query — on a 145k-asset company that is a 231 MB
        // response — and then keep the first 20.
        const r = await api.get('/assets', { params: { search: query.trim(), limit: 20 } });
        setResults(r.data.slice(0, 20));
      } catch {}
    }, 250);
    return () => clearTimeout(debounceRef.current);
  }, [query]);

  // ── Proximity polling loop ────────────────────────────────────────────────
  const stopTracking = useCallback(() => {
    setTracking(false);
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (geigerRef.current) geigerRef.current.stop();
    setProximity(null);
  }, []);

  const pollOnce = useCallback(async (tag) => {
    try {
      const r = await api.get('/raw-inventory/proximity', {
        params: { rfid_tag: tag, max_age_seconds: 30 },
      });
      if (r.data.found && !r.data.stale) {
        setProximity(r.data);
        if (audioOn && geigerRef.current) geigerRef.current.setRssi(r.data.rssi);
      } else {
        setProximity(prev => prev ? { ...prev, stale: true } : null);
        if (geigerRef.current) geigerRef.current.stop();
      }
    } catch {}
  }, [audioOn]);

  const startTracking = useCallback((asset) => {
    if (!asset?.rfid_tag) return;
    setTarget(asset);
    setTracking(true);
    setProximity(null);
    if (pollRef.current) clearInterval(pollRef.current);
    pollOnce(asset.rfid_tag);
    pollRef.current = setInterval(() => pollOnce(asset.rfid_tag), POLL_MS);
  }, [pollOnce]);

  // ── Radar sweep animation while tracking ──────────────────────────────────
  useEffect(() => {
    let frame;
    if (tracking) {
      const animate = () => { setRadarAngle(a => (a + 4) % 360); frame = requestAnimationFrame(animate); };
      frame = requestAnimationFrame(animate);
    }
    return () => cancelAnimationFrame(frame);
  }, [tracking]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (geigerRef.current) geigerRef.current.disable();
    };
  }, []);

  const toggleAudio = () => {
    if (audioOn) {
      geigerRef.current?.disable();
      geigerRef.current = new GeigerEngine();
      setAudioOn(false);
    } else {
      geigerRef.current?.enable();
      setAudioOn(true);
      // Confirmation chirp — proves the audio engine is alive even if no
      // scans have arrived yet to drive the geiger.
      try {
        geigerRef.current._click(880);
        setTimeout(() => geigerRef.current?._click(1100), 90);
      } catch {}
      // Re-prime if we already have a reading
      if (proximity?.rssi != null) geigerRef.current.setRssi(proximity.rssi);
    }
  };

  const band = proximity?.rssi != null ? rssiBand(proximity.rssi, proximity.bands || DEFAULT_BANDS) : null;

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <div className="page-title"><MdSearch /> Asset Search</div>
          <div className="page-subtitle">
            Live RSSI-based proximity finder. Reader/handheld scans push to the same backend the search reads from.
          </div>
        </div>
      </div>

      <div className="grid-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        {/* LEFT: search + results */}
        <div>
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-header">
              <div className="card-title"><MdSearch style={{ color: 'var(--accent)' }} /> Find Asset</div>
            </div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              <input
                className="form-control"
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="Type any partial: RFID tag, serial, name, asset tag, barcode…"
                style={{ flex: 1 }}
                autoFocus
              />
            </div>
            <div style={{ fontSize: 11, color: '#888', marginBottom: 8 }}>
              {query ? `${results.length} match${results.length === 1 ? '' : 'es'}` : 'Start typing to search'}
            </div>

            <div style={{ maxHeight: 360, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 6 }}>
              {results.length === 0 && query && (
                <div style={{ padding: 24, textAlign: 'center', color: '#888' }}>No matches</div>
              )}
              {results.map(a => (
                <div key={a.id}
                  onClick={() => startTracking(a)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '8px 12px', borderBottom: '1px solid var(--border-light)',
                    cursor: 'pointer',
                    background: target?.id === a.id ? '#e3f2fd' : 'transparent',
                  }}
                  onMouseEnter={e => { if (target?.id !== a.id) e.currentTarget.style.background = '#f8f9fb'; }}
                  onMouseLeave={e => { if (target?.id !== a.id) e.currentTarget.style.background = 'transparent'; }}
                >
                  <MdInventory style={{ color: 'var(--accent)', fontSize: 18, flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{a.asset_name}</div>
                    <div style={{ fontSize: 11, color: '#666', fontFamily: 'monospace' }}>
                      {a.rfid_tag}
                      {a.serial_number && ` · SN: ${a.serial_number}`}
                      {a.location_name && ` · ${a.location_name}`}
                    </div>
                  </div>
                  <span className={`badge status-${a.status}`}>{a.status}</span>
                </div>
              ))}
            </div>
          </div>

          {target && (
            <div className="card">
              <div className="card-header">
                <div className="card-title">Target</div>
                <button className="btn btn-outline btn-sm" onClick={stopTracking}>
                  <MdStop /> Stop tracking
                </button>
              </div>
              <div style={{
                background: 'linear-gradient(135deg, #e8f5e9, #f0fff4)',
                border: '1.5px solid #a5d6a7', borderRadius: 12, padding: 14,
              }}>
                <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 6 }}>{target.asset_name}</div>
                <div style={{ fontSize: 12, color: '#444', display: 'grid', gap: 4 }}>
                  <div><strong>RFID:</strong> <code style={{ color: 'var(--accent)' }}>{target.rfid_tag}</code></div>
                  {target.serial_number && <div><strong>Serial:</strong> <code>{target.serial_number}</code></div>}
                  {target.location_name && <div><MdLocationOn style={{ verticalAlign: 'middle' }} /> {target.location_name}</div>}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* RIGHT: radar + RSSI meter */}
        <div className="card">
          <div className="card-header">
            <div className="card-title">
              <MdRssFeed style={{ color: 'var(--accent)' }} /> Proximity Finder
              {tracking && (
                <span style={{
                  marginLeft: 10, fontSize: 10, padding: '2px 8px', borderRadius: 10,
                  background: wsConnected ? '#e8f5e9' : '#fff3e0',
                  color: wsConnected ? '#2e7d32' : '#ef6c00',
                  fontWeight: 700, letterSpacing: 0.4,
                }}>
                  {wsConnected ? '⚡ LIVE' : '⟳ POLLING'}
                </span>
              )}
            </div>
            {tracking && (
              <button
                className={`btn btn-sm ${audioOn ? 'btn-primary' : 'btn-outline'}`}
                onClick={toggleAudio}
                title={audioOn ? 'Mute geiger audio' : 'Enable geiger audio'}
              >
                {audioOn ? <MdVolumeUp /> : <MdVolumeOff />} Audio
              </button>
            )}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, padding: '20px 0' }}>
            {/* Radar */}
            <div style={{ position: 'relative', width: 240, height: 240 }}>
              {[240, 180, 120, 60].map((size, i) => (
                <div key={i} style={{
                  position: 'absolute', width: size, height: size, borderRadius: '50%',
                  border: `1px solid ${tracking ? 'rgba(33,150,243,0.3)' : 'rgba(33,150,243,0.15)'}`,
                  top: (240 - size) / 2, left: (240 - size) / 2,
                }} />
              ))}
              {tracking && (
                // Pivot is exactly the radar centre (50%/50% of the 240x240 box).
                // The line extends rightward from the pivot; rotating around (0,0)
                // of this 0×0 anchor keeps the sweep correctly centred.
                <div style={{
                  position: 'absolute', top: '50%', left: '50%',
                  width: 0, height: 0,
                  transformOrigin: '0 0', transform: `rotate(${radarAngle}deg)`,
                }}>
                  <div style={{
                    width: 110, height: 2,
                    background: 'linear-gradient(90deg, transparent, rgba(33,150,243,0.85))',
                    transform: 'translateY(-1px)',  // centre the 2px line on the rotation axis
                  }} />
                </div>
              )}
              {band && !proximity?.stale && (
                <div style={{
                  position: 'absolute',
                  width: 16, height: 16, borderRadius: '50%',
                  background: band.color,
                  boxShadow: `0 0 14px ${band.color}`,
                  top: 120 - (band.pct / 100) * 96,
                  left: '50%', transform: 'translateX(-50%)',
                  transition: 'top 0.3s ease, background 0.3s, box-shadow 0.3s',
                  animation: 'pulse 1s ease infinite',
                }} />
              )}
              <div style={{
                position: 'absolute', width: 14, height: 14, borderRadius: '50%',
                background: 'var(--accent)', top: '50%', left: '50%',
                transform: 'translate(-50%,-50%)',
                boxShadow: '0 0 10px var(--accent)',
              }} />
            </div>

            {/* Status text */}
            {!tracking ? (
              <div style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: 14 }}>
                Pick an asset on the left to start tracking
              </div>
            ) : !proximity ? (
              <div style={{ textAlign: 'center', color: '#666', fontSize: 13 }}>
                Waiting for a scan of <code style={{ color: 'var(--accent)' }}>{target?.rfid_tag}</code>…
                <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>
                  Hold the reader/handheld near the area and pull the trigger.
                </div>
                {audioOn && (
                  <div style={{ fontSize: 11, color: '#2e7d32', marginTop: 6, fontWeight: 600 }}>
                    🔊 Audio on — geiger ticks will start as soon as a scan arrives
                  </div>
                )}
              </div>
            ) : proximity.stale ? (
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: '#888' }}>NO RECENT SIGNAL</div>
                <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                  Last seen {proximity.age_seconds}s ago — keep scanning
                </div>
              </div>
            ) : (
              <div style={{ textAlign: 'center', width: 240 }}>
                <div style={{ fontSize: 32, fontWeight: 800, color: band.color, letterSpacing: '-0.5px' }}>
                  {band.label}
                </div>
                <div style={{ fontSize: 13, fontWeight: 600, color: band.color, marginTop: 2 }}>
                  RSSI {proximity.rssi} dBm
                </div>
                <div style={{ marginTop: 10 }}>
                  <div style={{ background: '#e0e6ef', borderRadius: 10, height: 12, overflow: 'hidden' }}>
                    <div style={{
                      height: '100%', borderRadius: 10, background: band.color,
                      width: `${band.pct}%`, transition: 'width 0.3s ease, background 0.3s',
                    }} />
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--text-muted)', marginTop: 4 }}>
                    <span>-90 dBm</span><span>-30 dBm</span>
                  </div>
                </div>
                <div style={{ fontSize: 11, color: '#888', marginTop: 8 }}>
                  Last scan {proximity.age_seconds}s ago
                  {proximity.scanned_by_name && ` · by ${proximity.scanned_by_name}`}
                </div>
              </div>
            )}

            {tracking && (
              <div style={{
                fontSize: 11, color: '#666', textAlign: 'center', maxWidth: 280,
                padding: 8, background: '#f8f9fb', borderRadius: 6,
              }}>
                <strong>How it works:</strong> Any reader (fixed Impinj or CX1500N handheld)
                that pushes scans to <code>POST /api/raw-inventory/scan</code> with RSSI feeds this meter live.
                Polling every {POLL_MS}ms.
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`@keyframes pulse { 0%,100%{opacity:1;transform:translateX(-50%) scale(1)} 50%{opacity:0.6;transform:translateX(-50%) scale(1.3)} }`}</style>
    </div>
  );
};

export default AssetSearch;
